
import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  schema?: object | object[];
  canonical?: string;
}

export function SEO({ title, description, keywords, image, schema, canonical }: SEOProps) {
  const siteUrl = 'https://jl-security.co.uk';
  const defaultImage = `${siteUrl}/og-image.jpg`;
  const ogImage = image || defaultImage;
  const canonicalUrl = canonical || `${siteUrl}${window.location.pathname}`;
  const lastModified = new Date().toISOString().split('T')[0];

  useEffect(() => {
    document.title = title;

    const updateMetaTag = (selector: string, attribute: string, content: string) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        if (attribute === 'property') {
          element.setAttribute('property', selector.replace('meta[property="', '').replace('"]', ''));
        } else {
          element.setAttribute('name', selector.replace('meta[name="', '').replace('"]', ''));
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    updateMetaTag('meta[name="title"]', 'name', title);
    updateMetaTag('meta[name="description"]', 'name', description);
    if (keywords) {
      updateMetaTag('meta[name="keywords"]', 'name', keywords);
    }
    updateMetaTag('meta[name="author"]', 'name', 'JL Security');
    updateMetaTag('meta[name="last-modified"]', 'name', lastModified);
    updateLinkTag('canonical', canonicalUrl);

    updateMetaTag('meta[name="geo.region"]', 'name', 'GB-WLS');
    updateMetaTag('meta[name="geo.placename"]', 'name', 'North Wales');
    updateMetaTag('meta[name="geo.position"]', 'name', '53.2274;-4.1293');
    updateMetaTag('meta[name="ICBM"]', 'name', '53.2274, -4.1293');

    updateMetaTag('meta[property="og:type"]', 'property', 'website');
    updateMetaTag('meta[property="og:url"]', 'property', canonicalUrl);
    updateMetaTag('meta[property="og:title"]', 'property', title);
    updateMetaTag('meta[property="og:description"]', 'property', description);
    updateMetaTag('meta[property="og:image"]', 'property', ogImage);
    updateMetaTag('meta[property="og:site_name"]', 'property', 'JL Security');
    updateMetaTag('meta[property="og:locale"]', 'property', 'en_GB');

    updateMetaTag('meta[property="twitter:card"]', 'property', 'summary_large_image');
    updateMetaTag('meta[property="twitter:url"]', 'property', canonicalUrl);
    updateMetaTag('meta[property="twitter:title"]', 'property', title);
    updateMetaTag('meta[property="twitter:description"]', 'property', description);
    updateMetaTag('meta[property="twitter:image"]', 'property', ogImage);

    // Remove old schema scripts
    const oldSchemas = document.querySelectorAll('script[data-seo-schema]');
    oldSchemas.forEach(el => el.remove());

    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s, i) => {
        const schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        schemaScript.setAttribute('data-seo-schema', `${i}`);
        schemaScript.textContent = JSON.stringify(s);
        document.head.appendChild(schemaScript);
      });
    }
  }, [title, description, keywords, ogImage, canonicalUrl, lastModified, schema]);

  return null;
}
